import { useEffect, useState } from "react";
import { useGetBillOfMaterialSummaryHistory } from "../../../../../api/billOfMaterialSummary";
import { useGetProductSummaryHistory } from "../../../../../api/productSummary";
import { Button } from "../../../../../components/ui/common/button";
import { Card, CardContent } from "../../../../../components/ui/common/card";
import { Skeleton } from "../../../../../components/ui/common/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "../../../../../components/ui/common/table";
import { CALCULATED_VERSION } from "../../../../../config/constants";
import { IngredientDataType } from "../../../../../config/enums/IngredientType.enum";
import { ROLES } from "../../../../../config/permission-maps";
import { CALCULATED_VERSION_PREFIX } from "../../../../../config/tableColConstant";
import { getUserRolesInLocal } from "../../../../../utils/cacheStorage";
import { ImpactCardsGrid } from "../../../../../components/impactCardsGrid/ImpactCardsGrid";
import { ImpactData } from "../../../../productSummary/types";
import { IngredientRowViewModel } from "../../../ingredientList/types/ingredientListView.types";
import { IngredientDetailsFormatter } from "../../services/IngredientDetailsFormatter";
import {
  INGREDIENT_DETAILS_TABLE_CONTAINER_CLASS,
  INGREDIENT_DETAILS_TABLE_HEADER_ROW_CLASS,
  INGREDIENT_DETAILS_TABLE_ROW_CLASS
} from "../shared/tableStyles";

interface HistoryTabProps {
  ingredientDetails: IngredientRowViewModel | undefined;
}

/** "History" tab — Tailwind rebuild of the legacy `IngredientSummaryHistory.tsx`. */
const HistoryTab = ({ ingredientDetails }: HistoryTabProps) => {
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(5);
  const [totalSummaryHistory, setTotalSummaryHistory] = useState(0);
  const [totalImpact, setTotalImpact] = useState<ImpactData[]>([]);

  const localUserRole = getUserRolesInLocal();
  const checkIsUserSupplier = localUserRole?.[0] === ROLES.supplier;

  const details = ingredientDetails?.supplierIngredientDetails as
    | Record<string, unknown>
    | null
    | undefined;
  const isSupplier = details?.data_type === IngredientDataType.SUPPLIER;

  const ingredientId = ingredientDetails?.ingredient_id;
  const supplierId = ingredientDetails?.supplier_id || undefined;
  const productSummaryRef = details?.product_summary as
    | { productId?: string; salesUnitId?: string }
    | undefined;

  const productSummaryHistoryData = useGetProductSummaryHistory({
    pageNumber,
    pageSize,
    productId: productSummaryRef?.productId,
    salesUnitId: productSummaryRef?.salesUnitId,
    isSupplier
  });

  const billOfMaterialSummaryData = useGetBillOfMaterialSummaryHistory({
    pageNumber,
    pageSize,
    ingredientId,
    supplierId,
    isSupplier
  });

  const productSummaryHistory = isSupplier
    ? productSummaryHistoryData.data
    : billOfMaterialSummaryData.data;

  const isLoading = isSupplier
    ? productSummaryHistoryData.isFetching
    : billOfMaterialSummaryData.isFetching;

  const historyList = isSupplier
    ? productSummaryHistory?.result?.productSummaryHistory || []
    : productSummaryHistory?.result?.billOfMaterialSummaryHistory || [];

  useEffect(() => {
    if (productSummaryHistory) {
      setPageNumber(productSummaryHistory.page);
      setTotalSummaryHistory(productSummaryHistory.total);
    }
  }, [productSummaryHistory]);

  if (checkIsUserSupplier) {
    return (
      <p className="tw-rounded-md tw-border tw-border-amber-300 tw-bg-amber-50 tw-px-4 tw-py-3 tw-text-sm tw-text-amber-800">
        Calculated: Upgrade to see results
      </p>
    );
  }

  const totalPages = Math.max(1, Math.ceil(totalSummaryHistory / pageSize));

  return (
    <div className="tw-flex tw-flex-col tw-gap-3">
      <div className={INGREDIENT_DETAILS_TABLE_CONTAINER_CLASS}>
        <Table>
          <TableHeader>
            <TableRow className={INGREDIENT_DETAILS_TABLE_HEADER_ROW_CLASS}>
              <TableHead>Date</TableHead>
              <TableHead>Climate Change Impact (kgCO2eq/kg)</TableHead>
              <TableHead>Calculated Version</TableHead>
              <TableHead className="tw-text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow className={INGREDIENT_DETAILS_TABLE_ROW_CLASS}>
                <TableCell colSpan={4}>
                  <Skeleton className="tw-h-16 tw-w-full" />
                </TableCell>
              </TableRow>
            ) : (
              historyList.map((data: Record<string, any>, index: number) => (
                <TableRow key={index} className={INGREDIENT_DETAILS_TABLE_ROW_CLASS}>
                  <TableCell>
                    {IngredientDetailsFormatter.formatDateTime(data.climateChange?.createdDate)}
                  </TableCell>
                  <TableCell>
                    {data.climateChange?.measure?.toFixed(2)} {data.climateChange?.unit}
                  </TableCell>
                  <TableCell>
                    {CALCULATED_VERSION_PREFIX} {data?.calculateVersion || CALCULATED_VERSION}
                  </TableCell>
                  <TableCell className="tw-text-right">
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => setTotalImpact((data.totalImpact as ImpactData[]) || [])}
                    >
                      Details
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 ? (
        <div className="tw-flex tw-items-center tw-justify-end tw-gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={pageNumber <= 1}
            onClick={() => setPageNumber(page => page - 1)}
          >
            Previous
          </Button>
          <span className="tw-text-sm tw-text-muted-foreground">
            Page {pageNumber} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={pageNumber >= totalPages}
            onClick={() => setPageNumber(page => page + 1)}
          >
            Next
          </Button>
        </div>
      ) : null}

      {totalImpact.length > 0 ? (
        <Card className="tw-border-border tw-bg-white tw-p-1">
          <CardContent className="tw-p-2">
            <ImpactCardsGrid impactData={totalImpact} />
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
};

export default HistoryTab;
